import type { PaymentEnrollmentFormState } from "./redux/slices/paymentEnrollmentForm";
import { SessionStorageKey } from "./constants";
import { resolveValidDisplayDob } from "./dobUtils";
import { getFullName } from "./name";
import { getNoomSessionStorage } from "./noomSessionStorage";

export const CHECKOUT_ENROLLMENT_PREFILL_VERSION = 1;

export interface CheckoutEnrollmentPrefill {
  version: typeof CHECKOUT_ENROLLMENT_PREFILL_VERSION;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  phoneNumberCountryCode?: string;
}

type EnrollmentInfo = PaymentEnrollmentFormState["enrollmentInfo"];
type PrefillableEnrollmentField =
  | "firstName"
  | "lastName"
  | "dateOfBirth"
  | "phoneNumber"
  | "phoneNumberCountryCode";

export function getCheckoutEnrollmentPrefill():
  | CheckoutEnrollmentPrefill
  | undefined {
  const raw: unknown = getNoomSessionStorage(
    SessionStorageKey.checkoutEnrollmentPrefill
  );

  if (!isRecord(raw) || raw.version !== CHECKOUT_ENROLLMENT_PREFILL_VERSION) {
    return undefined;
  }

  const dateOfBirth = resolveValidDisplayDob(raw.dateOfBirth);

  return {
    version: CHECKOUT_ENROLLMENT_PREFILL_VERSION,
    ...optionalTrimmedString("firstName", raw.firstName),
    ...optionalTrimmedString("lastName", raw.lastName),
    ...(dateOfBirth && { dateOfBirth }),
    ...optionalTrimmedString("phoneNumber", raw.phoneNumber),
    ...optionalLowercaseString(
      "phoneNumberCountryCode",
      raw.phoneNumberCountryCode
    ),
  };
}

/**
 * Adds V2 bridge values only where checkout has no value yet.
 * Existing Redux/user-entered values always win.
 */
export function mergeCheckoutEnrollmentPrefill(
  current: EnrollmentInfo,
  prefill: CheckoutEnrollmentPrefill | undefined
): EnrollmentInfo {
  if (!prefill) {
    return current;
  }

  const next: EnrollmentInfo = {
    ...current,
    ...whenBlank(current.firstName, "firstName", prefill.firstName),
    ...whenBlank(current.lastName, "lastName", prefill.lastName),
    ...whenBlank(current.dateOfBirth, "dateOfBirth", prefill.dateOfBirth),
    ...whenBlank(current.phoneNumber, "phoneNumber", prefill.phoneNumber),
    ...whenBlank(
      current.phoneNumberCountryCode,
      "phoneNumberCountryCode",
      prefill.phoneNumberCountryCode
    ),
  };

  if (!current.name.trim() && (next.firstName || next.lastName)) {
    next.name = getFullName(next.firstName, next.lastName);
  }

  return next;
}

export function shouldApplyNoomVibePhone(input: {
  enrollmentPhoneNumber: string;
  checkoutPrefillPhoneNumber?: string;
  noomVibePhoneNumber?: string;
  noomVibeCountryCode?: string;
  inputChanged: boolean;
}): boolean {
  return Boolean(
    input.noomVibePhoneNumber &&
      input.noomVibeCountryCode &&
      !input.inputChanged &&
      !input.enrollmentPhoneNumber &&
      !input.checkoutPrefillPhoneNumber
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function optionalTrimmedString<Key extends string>(
  key: Key,
  value: unknown
): Partial<Record<Key, string>> {
  if (typeof value !== "string") {
    return {};
  }

  const trimmed = value.trim();
  return trimmed ? ({ [key]: trimmed } as Partial<Record<Key, string>>) : {};
}

function optionalLowercaseString<Key extends string>(
  key: Key,
  value: unknown
): Partial<Record<Key, string>> {
  const normalized = optionalTrimmedString(key, value);
  return normalized[key]
    ? ({
        [key]: normalized[key]?.toLowerCase(),
      } as Partial<Record<Key, string>>)
    : {};
}

function whenBlank<Key extends PrefillableEnrollmentField>(
  currentValue: string,
  key: Key,
  prefillValue: string | undefined
): Partial<Pick<EnrollmentInfo, Key>> {
  return !currentValue.trim() && prefillValue
    ? ({ [key]: prefillValue } as Partial<Pick<EnrollmentInfo, Key>>)
    : {};
}
